import { differenceInDays, differenceInMonths, differenceInWeeks } from 'date-fns'

interface Dependencies {
  now: () => Date
}

export interface RelativePastTimeResolver {
  resolve: (pastTime: Date) => {
    daysAgo: string | null
    weeksAgo: string | null
    monthsAgo: string | null
  }
}

export function createRelativePastTimeResolver(dependencies: Partial<Dependencies> = {}): RelativePastTimeResolver {
  const { now = () => new Date() } = dependencies as Dependencies

  function resolve(pastTime: Date) {
    if (isFutureDate(pastTime)) {
      return {
        daysAgo: null,
        weeksAgo: null,
        monthsAgo: null,
      }
    }

    const daysDifference = differenceInDays(now(), pastTime)

    if (isLessThanAWeekAgo(pastTime)) {
      return {
        daysAgo: daysDifference.toString(),
        weeksAgo: null,
        monthsAgo: null,
      }
    }

    const weeksDifference = differenceInWeeks(now(), pastTime)
    const monthsDifference = differenceInMonths(now(), pastTime)

    if (isMoreThanAMonthAgo(pastTime)) {
      return {
        daysAgo: daysDifference.toString(),
        weeksAgo: weeksDifference.toString(),
        monthsAgo: monthsDifference.toString(),
      }
    }

    return {
      // Is less than a month ago
      daysAgo: daysDifference.toString(),
      weeksAgo: weeksDifference.toString(),
      monthsAgo: null,
    }
  }

  function isFutureDate(pastTime: Date) {
    const daysDifference = differenceInDays(now(), pastTime)
    return daysDifference < 0
  }

  function isLessThanAWeekAgo(pastTime: Date) {
    const daysDifference = differenceInDays(now(), pastTime)
    return daysDifference < 7
  }

  function isMoreThanAMonthAgo(pastTime: Date) {
    const monthsDifference = differenceInMonths(now(), pastTime)
    return monthsDifference > 0
  }

  return { resolve }
}

export const relativePastTimeResolver = createRelativePastTimeResolver()
